<template>
  <div
    v-test="{ id: 'trayVials' }"
    class="tray-vials"
    :style="styleGrid"
    :class="{ 'tray-vials--in-popup': this.isInPopup }"
  >
    <div v-if="!isAxislessTray" class="tray-vials__stub" />
    <template v-if="!isAxislessTray">
      <div
        v-for="(_, indexColumn) of columns"
        :key="`label-x-${indexColumn}`"
        class="tray-vials__label"
      >
        {{ LABELS_X[indexColumn] }}
      </div>
    </template>
    <template v-for="(_, indexRow) of rows">
      <div
        v-if="!isAxislessTray"
        :key="`label-y-${indexRow}`"
        class="tray-vials__label tray-vials__label--letter"
      >
        {{ LABELS_Y[indexRow] }}
      </div>
      <Vial
        v-for="column of columns"
        :key="`${LABELS_Y[indexRow]}${column}`"
        v-model="_checkedVial"
        v-test="{ id: 'vial' }"
        :isDisabled="
          isDisabled ||
          usedVialsMap[isSingleRowTray ? 'W' + column : `${LABELS_Y[indexRow]}${column}`]
        "
        :isUsed="usedVialsMap[isSingleRowTray ? 'W' + column : `${LABELS_Y[indexRow]}${column}`]"
        :isProcessing="isProcessing"
        :value="isSingleRowTray ? 'W' + column : `${LABELS_Y[indexRow]}${column}`"
        :label="isNumberedTray ? indexRow * columns + column : null"
        class="tray-vials__vial"
        data-test-id="vial"
      />
    </template>
  </div>
</template>

<script>
  import Vial from '@/uikitProject/vials/shared/Vial';
  import { TRAY_CONFIGS } from '@/constants/hardware/trays';

  import testDirective from '@/directives/test';

  const EVENT_MODEL = 'model';

  const DEFAULT_CONFIG = {
    rows: 6,
    columns: 8,
  };

  const LABELS_X = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const LABELS_Y = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

  const getAllPossibleVials = (labelsX, labelsY) =>
    labelsY.flatMap((y) => labelsX.map((x) => `${y}${x}`));

  export const ALL_POSSIBLE_VIALS = getAllPossibleVials(LABELS_X, LABELS_Y);

  export default {
    name: 'TrayVials',

    directives: { test: testDirective },

    components: {
      Vial,
    },

    model: {
      prop: 'checkedVial',
      event: EVENT_MODEL,
    },

    props: {
      checkedVial: {
        type: [String, Number],
      },
      // Config with number of rows and columns.
      // Format: 96_8x12
      config: {
        required: true,
        validator: (value) => typeof value === 'string' || value === null,
      },
      isDisabled: {
        type: Boolean,
      },
      isProcessing: {
        type: Boolean,
      },
      isInPopup: {
        type: Boolean,
      },
      usedVials: {
        type: Array,
        default: () => [],
      },
    },

    data: () => ({
      LABELS_X,
      LABELS_Y,
    }),

    computed: {
      _checkedVial: {
        get() {
          return this.checkedVial;
        },
        set(value) {
          this.$emit(EVENT_MODEL, value);
        },
      },

      styleGrid() {
        return {
          gridTemplateColumns: `repeat(${this.columns + (this.isAxislessTray ? 0 : 1)}, 1fr)`,
          gridTemplateRows: `repeat(${this.rows + (this.isAxislessTray ? 0 : 1)}, auto)`,
        };
      },

      configParsed() {
        return this.config?.match(/\d+_(?<rows>\d+)x(?<columns>\d+)/)?.groups ?? DEFAULT_CONFIG;
      },

      columns() {
        return Number(this.configParsed.columns);
      },
      rows() {
        return Number(this.configParsed.rows);
      },
      isSingleRowTray() {
        return this.configParsed.rows < 2;
      },
      isNumberedTray() {
        return this.config === TRAY_CONFIGS.TRAY_54_6X9;
      },
      isAxislessTray() {
        return this.isSingleRowTray || this.isNumberedTray;
      },

      usedVialsMap() {
        return Object.fromEntries(this.usedVials.map((vial) => [vial, true]));
      },
    },
  };
</script>

<style lang="scss" scoped>
  .tray-vials {
    display: grid;

    &--in-popup {
      @media (max-width: $screen-sm-max) {
        font-size: $size-sm;
      }
    }

    &__label {
      align-self: center;
      justify-self: center;

      &--letter {
        @media (max-width: $screen-xxs-max) {
          margin-right: 3px;
        }
      }
    }

    &__row {
      display: flex;
      flex: 1;
    }

    &__vial {
      width: 100%;
      aspect-ratio: 1 / 1;
      margin: 0;
    }
  }
</style>
